import { BASE_STATS, BASE_STATS_INFO, BaseStatInfo } from "data/baseStats";
import { BATTLE_RATINGS } from "data/battleRatings";
import { BUILDING_TYPES, BUILDINGS } from "data/buildings";
import {
  DERIVED_STATS,
  DerivedStatInfo,
  RELATIVE_FIGHT_STATS,
  RelativeFightStatInfo,
  STAT_RANGES,
  StatRangeInfo,
} from "data/derivedStats";
import { PLANETS, SECTORS } from "data/locations";
import { MUSIC } from "data/music";
import { NPCS } from "data/npcs";
import { PARTS } from "data/parts";
import { PILOTS } from "data/pilots";
import { PROPERTIES } from "data/properties";
import { SHIPS, ShipsData } from "data/ships";
import {
  BattleRatingModel,
  BattleRatings,
  BattleRatingsData,
  BuildingModel,
  BuildingTypeModel,
  BuildingTypes,
  CharacterBaseStats,
  CharacterDerivedStats,
  DerivedStatSets,
  InstalledUpgradesData,
  MusicTrackModel,
  NPCModel,
  PartModel,
  PartTypes,
  PilotModel,
  PilotTypes,
  PlanetModel,
  Planets,
  Properties,
  PropertyModel,
  RelativeFightStats,
  SectorModel,
  ShipModel,
  ShipTypes,
  StatRanges,
  StatRangeSets,
} from "types";
import { roundOneDecimal } from "../utils/formatters";
import { getAllMobsData, getBattleRatingData, getMobData } from "./fight";
import { getInstalledUpgradesStats } from "./item";
import { getSkillData } from "./skill";

export const CREDITS_PER_STAT_TOKEN = 20;
export const CREDITS_PER_SKILL_TOKEN = 20;
export const MAX_BASE_STAT_VALUE = 100;
export const MOB_BASE_STATS_MODIFIERS_TOTAL = 15;
export const MOB_BASE_STATS_COSTS_TOTAL = 10;
export const PILOT_SKILL_COSTS_TOTAL = 10;
export const MEDALLIONS_PER_MOB = 5;

export const getAvgAttackDamage = (
  minAttackDamage: number,
  maxAttackDamage: number
) => {
  const attackDamageInfo = getDerivedStatData("maxAttackDamage");

  const avgAttackDamage = (minAttackDamage + maxAttackDamage) / 2;

  return attackDamageInfo.rounder(avgAttackDamage);
};

export const getAvgWeakenParts = (
  minWeakenParts: number,
  maxWeakenParts: number
) => {
  const weakenPartsInfo = getDerivedStatData("maxWeakenParts");
  const avgWeakenParts = (minWeakenParts + maxWeakenParts) / 2;

  return weakenPartsInfo.rounder(avgWeakenParts);
};

export const getPilotData = (pilot: PilotTypes): PilotModel => {
  return PILOTS[pilot];
};

export const getAllPilotsData = (): Record<PilotTypes, PilotModel> => {
  return PILOTS;
};

export const getShipData = (ship: ShipTypes): ShipModel => {
  return SHIPS[ship];
};

export const getAllShipsData = (): ShipsData => {
  return SHIPS;
};

export const getPlanetData = (planet: Planets): PlanetModel => {
  return PLANETS[planet];
};

export const getAllPlanetsData = (): Record<string, PlanetModel> => {
  const activePlanets = Object.fromEntries(
    Object.entries(PLANETS).filter(([_, planet]) => !planet.isDisabled)
  );
  return activePlanets;
};

export const getNPCData = (npc: string): NPCModel => {
  return NPCS[npc];
};

export const getAreaData = (sector: string): SectorModel => {
  return SECTORS[sector];
};

export const getBuildingData = (building: string): BuildingModel => {
  return BUILDINGS[building];
};

export const getBuildingTypeData = (
  buildingType: BuildingTypes
): BuildingTypeModel => {
  return BUILDING_TYPES[buildingType];
};

export const getMusicData = (track: string): MusicTrackModel => {
  return MUSIC[track];
};

export const getPartData = (slug: PartTypes): PartModel => {
  return PARTS[slug];
};

export const getPropertyData = (slug: Properties): PropertyModel => {
  return PROPERTIES[slug];
};

export const getBaseStatData = (
  baseStat: keyof CharacterBaseStats
): BaseStatInfo => {
  return BASE_STATS_INFO[baseStat];
};

export const getDerivedStatData = (
  stat: keyof CharacterDerivedStats
): DerivedStatInfo => {
  return DERIVED_STATS[stat];
};

export const getStatRangeData = (
  statRange: keyof StatRanges
): StatRangeInfo => {
  return STAT_RANGES[statRange];
};

export const getRelativeFightStatData = (
  fightStat: keyof RelativeFightStats
): RelativeFightStatInfo => {
  return RELATIVE_FIGHT_STATS[fightStat];
};

export const getRandomBaseStat = (): keyof CharacterBaseStats => {
  const baseStats = Object.values(BASE_STATS);

  const randomIndex = Math.floor(Math.random() * baseStats.length);

  const baseStat = baseStats[randomIndex];

  return baseStat;
};

export const getMaxBaseStatValue = (totalBaseStats: CharacterBaseStats) => {
  const maxValue = Math.max(...Object.values(totalBaseStats));

  return maxValue;
};

export const getShipDamage = (health: number, maxHealth: number): number => {
  const shipDamage = maxHealth - health;

  return shipDamage;
};

export const hasAnimation = (
  animations: string[] = [],
  animation: string
): boolean => {
  return animations.includes(animation);
};

export const getFilteredBattleRatings = (battleRatings: BattleRatingsData) => {
  const allMobsSlugs = Object.keys(getAllMobsData());

  const filteredBattleRatings = Object.keys(battleRatings).reduce(
    (acc, key) => {
      if (allMobsSlugs.includes(key)) {
        acc[key] = battleRatings[key];
      }
      return acc;
    },
    {} as BattleRatingsData
  );

  return filteredBattleRatings;
};

export const getTotalMedallions = (battleRatings: BattleRatingsData) => {
  // Get total medallions based on earned battle ratings
  let totalMedallions = 0;

  for (const mobSlug in battleRatings) {
    const previousBattleRating = battleRatings[mobSlug];

    for (const key in BATTLE_RATINGS) {
      const ratingKey = key as BattleRatings;
      const ratingData = BATTLE_RATINGS[ratingKey] as BattleRatingModel;

      const { value: previousRatingValue } =
        getBattleRatingData(previousBattleRating);

      if (previousRatingValue >= ratingData.value) {
        totalMedallions += ratingData.rewardMultiplier;
      }
    }
  }

  return totalMedallions;
};

export const getStatsMedallionsSpent = (
  enhancedBaseStats: CharacterBaseStats,
  ship: ShipTypes
): number => {
  const propertyInfo = getPropertyData("medallions");

  let totalTokensSpent = 0;
  const statsCosts = getShipData(ship).baseStatsCosts;

  // Figure out number of tokens to return - with differing stat costs
  for (const key in enhancedBaseStats) {
    const statSlug = key as keyof CharacterBaseStats;
    const enhancedStatValue = enhancedBaseStats[statSlug];

    const tokenCost = enhancedStatValue * statsCosts[statSlug];
    totalTokensSpent += tokenCost;
  }

  return propertyInfo.rounder(totalTokensSpent);
};

export const getTotalPossibleMedallions = () => {
  const allMobsData = getAllMobsData();

  const possibleMedallions =
    Object.keys(allMobsData).length * MEDALLIONS_PER_MOB;

  return possibleMedallions;
};

// BASE AND DERIVED STATS

export const calculateTotalBaseStats = (
  ship: ShipTypes,
  enhancedBaseStats: CharacterBaseStats
): CharacterBaseStats => {
  // Combine ship's initial stats with earned added base stats
  const { startingBaseStats } = getShipData(ship);

  return {
    firepower: startingBaseStats.firepower + enhancedBaseStats.firepower,
    resilience: startingBaseStats.resilience + enhancedBaseStats.resilience,
    speed: startingBaseStats.speed + enhancedBaseStats.speed,
    precision: startingBaseStats.precision + enhancedBaseStats.precision,
    energy: startingBaseStats.energy + enhancedBaseStats.energy,
  };
};

export const calculateCurrentBaseStats = (
  totalBaseStats: CharacterBaseStats,
  weakenedBaseStats: CharacterBaseStats
): CharacterBaseStats => {
  return {
    firepower: roundOneDecimal(
      totalBaseStats.firepower - weakenedBaseStats.firepower
    ),
    resilience: roundOneDecimal(
      totalBaseStats.resilience - weakenedBaseStats.resilience
    ),
    speed: roundOneDecimal(totalBaseStats.speed - weakenedBaseStats.speed),
    precision: roundOneDecimal(
      totalBaseStats.precision - weakenedBaseStats.precision
    ),
    energy: roundOneDecimal(totalBaseStats.energy - weakenedBaseStats.energy),
  };
};

export const calculateMobSkillMedallions = (skills: string[]): number => {
  return skills.reduce((acc, skill) => {
    const skillData = getSkillData(skill);
    return acc + (skillData.isDefault ? 0 : skillData.medallions);
  }, 0);
};

export const calculateMobBaseStats = (
  medallions: number,
  baseStatsModifiers: CharacterBaseStats,
  baseStatsCosts: CharacterBaseStats,
  skills: string[]
): CharacterBaseStats => {
  // Mob base stats depend on medallions and modifiers
  // Total modifiers should add up to 15

  const skillMedallions = calculateMobSkillMedallions(skills);

  // Should take into account cost - 1 medallion doesn't equal 1 stat
  const statMedallions = Math.max(0, medallions - skillMedallions);

  // Put together initial mob base stats object
  const mobBaseStats = Object.values(BASE_STATS).reduce(
    (mobBaseStats, baseStat) => {
      const mobBaseStat = calculateMobBaseStat(
        baseStatsModifiers[baseStat],
        baseStatsCosts[baseStat],
        statMedallions
      );
      return {
        ...mobBaseStats,
        [baseStat]: mobBaseStat,
      };
    },
    { firepower: 0, resilience: 0, speed: 0, precision: 0, energy: 0 }
  );

  return mobBaseStats;
};

const calculateMobBaseStat = (
  modifier: number,
  cost: number,
  totalMedallions: number
): number => {
  // Add boost of 10 medallions to counter character's initial 15
  const initialStatValue = modifier / 1.5;

  return roundOneDecimal(
    Math.min(
      initialStatValue +
        (modifier / MOB_BASE_STATS_MODIFIERS_TOTAL) * (totalMedallions / cost),
      MAX_BASE_STAT_VALUE
    )
  );
};

export const calculateInitialMobStats = (mobSlug: string) => {
  const {
    medallions,
    baseStatsModifiers,
    baseStatsCosts,
    installedUpgrades,
    skills,
  } = getMobData(mobSlug);

  const totalBaseStats = calculateMobBaseStats(
    medallions,
    baseStatsModifiers,
    baseStatsCosts,
    skills
  );

  const derivedStats = getDerivedStatSets(
    totalBaseStats,
    totalBaseStats,
    installedUpgrades
  );

  return {
    totalBaseStats,
    derivedStats,
  };
};

// Calculate Derived Stats

export const getBaseDerivedStats = (
  baseStats: CharacterBaseStats
): CharacterDerivedStats => {
  const stats = Object.entries(DERIVED_STATS).reduce((acc, [key, value]) => {
    const statType = key as keyof CharacterDerivedStats;
    const statInfo = value as DerivedStatInfo;
    return {
      ...acc,
      [statType]: calculateBaseDerivedStat(
        statInfo,
        baseStats[statInfo.baseStat]
      ),
    };
  }, {} as any);

  return stats;
};

export const calculateBaseDerivedStat = (
  derivedStat: DerivedStatInfo,
  baseStatValue: number
): number => {
  return derivedStat.rounder(
    derivedStat.startingValue + baseStatValue * derivedStat.incrementValue
  );
};

const calculateCompleteDerivedStat = (
  derivedStat: DerivedStatInfo,
  baseDerivedStat: number,
  upgradeValue: number
) => {
  return derivedStat.rounder(baseDerivedStat + upgradeValue);
};

export const getCompleteDerivedStats = (
  baseDerivedStats: CharacterDerivedStats,
  upgrades: CharacterDerivedStats
): CharacterDerivedStats => {
  const stats = Object.entries(DERIVED_STATS).reduce((acc, [key, value]) => {
    const statType = key as keyof CharacterDerivedStats;
    const statInfo = value as DerivedStatInfo;
    return {
      ...acc,
      [statType]: calculateCompleteDerivedStat(
        statInfo,
        baseDerivedStats[statType],
        upgrades[statType]
      ),
    };
  }, {} as any);

  // Make minimum attack damage always less than maximum
  if (stats.minAttackDamage > stats.maxAttackDamage) {
    stats.minAttackDamage = stats.maxAttackDamage;
  }
  // Make minimum weaken parts always less than maximum
  if (stats.minWeakenParts > stats.maxWeakenParts) {
    stats.minWeakenParts = stats.maxWeakenParts;
  }

  return stats;
};

export const getDerivedStatSets = (
  currentBaseStats: CharacterBaseStats,
  totalBaseStats: CharacterBaseStats,
  installedUpgrades: InstalledUpgradesData
): DerivedStatSets => {
  // Calculate current derived stat ranges
  const currentBaseDerivedStats = getBaseDerivedStats(currentBaseStats);
  const currentUpgradesStats = getInstalledUpgradesStats(
    installedUpgrades,
    currentBaseStats,
    totalBaseStats
  );
  const currentCompleteStats = getCompleteDerivedStats(
    currentBaseDerivedStats,
    currentUpgradesStats
  );

  // Calculate total derived stat ranges
  const totalBaseDerivedStats = getBaseDerivedStats(totalBaseStats);
  const totalUpgradesStats = getInstalledUpgradesStats(
    installedUpgrades,
    totalBaseStats,
    totalBaseStats
  );
  const totalCompleteStats = getCompleteDerivedStats(
    totalBaseDerivedStats,
    totalUpgradesStats
  );

  return {
    current: {
      base: currentBaseDerivedStats,
      upgrades: currentUpgradesStats,
      complete: currentCompleteStats,
    },
    total: {
      base: totalBaseDerivedStats,
      upgrades: totalUpgradesStats,
      complete: totalCompleteStats,
    },
  };
};

export const getStatRanges = (
  derivedStats: CharacterDerivedStats
): StatRanges => {
  const { maxAttackDamage, minAttackDamage, maxWeakenParts, minWeakenParts } =
    derivedStats;

  const attackDamageRangeInfo = getStatRangeData("attackDamageRange");
  const weakenPartsRangeInfo = getStatRangeData("weakenPartsRange");

  return {
    attackDamageRange: {
      minStat: "minAttackDamage",
      maxStat: "maxAttackDamage",
      min: attackDamageRangeInfo.rounder(minAttackDamage),
      max: attackDamageRangeInfo.rounder(maxAttackDamage),
    },
    weakenPartsRange: {
      minStat: "minWeakenParts",
      maxStat: "maxWeakenParts",
      min: weakenPartsRangeInfo.rounder(minWeakenParts),
      max: weakenPartsRangeInfo.rounder(maxWeakenParts),
    },
  };
};

export const getDerivedStatRangeSets = (
  statSets: DerivedStatSets
): StatRangeSets => {
  // Calculate current derived stat ranges
  const currentBaseDerivedRanges = getStatRanges(statSets.current.base);
  const currentUpgradesRanges = getStatRanges(statSets.current.upgrades);
  const currentCompleteRanges = getStatRanges(statSets.current.complete);

  // Calculate total derived stat ranges
  const totalBaseDerivedRanges = getStatRanges(statSets.total.base);
  const totalUpgradesRanges = getStatRanges(statSets.total.upgrades);
  const totalCompleteRanges = getStatRanges(statSets.total.complete);

  return {
    current: {
      base: currentBaseDerivedRanges,
      upgrades: currentUpgradesRanges,
      complete: currentCompleteRanges,
    },
    total: {
      base: totalBaseDerivedRanges,
      upgrades: totalUpgradesRanges,
      complete: totalCompleteRanges,
    },
  };
};
